

import { useMemo } from "react";
import {
  calculatePrice,
  calculateFinalDimensions,
} from "../lib/pricingService";
import { getOptionData } from "../utils/getOptionsData";
import { IProduct } from "../types/types";

export const useProductCalculations = (
  product: IProduct,
  selectedCustomizations: { [key: string]: string },
  isPremiumEnabled: boolean,
  isWholesaleUser: boolean
) => {
  return useMemo(() => {
    const availabilityMap: Record<string, Record<string, boolean>> = {};
    const optionTypeMap = new Map<string, string>();

    product.customization?.forEach((group) => {
      group.options
        .filter((opt) => !(!isWholesaleUser && opt.isWholeSaleOnly))
        .forEach((opt) => optionTypeMap.set(opt.value, group.type));
    });

    product.customization?.forEach((group) => {
      availabilityMap[group.type] = {};
      group.options
        .filter((opt) => !(!isWholesaleUser && opt.isWholeSaleOnly))
        .forEach((option) => {
          if (!option.availableFor || option.availableFor.length === 0) {
            availabilityMap[group.type][option.value] = true;
          } else {
            const groupedDependencies = new Map<string, string[]>();
            option.availableFor.forEach((depValue) => {
              const type = optionTypeMap.get(depValue);
              if (type) {
                if (!groupedDependencies.has(type))
                  groupedDependencies.set(type, []);
                groupedDependencies.get(type)!.push(depValue);
              }
            });

            let allGroupsMet = true;
            for (const [type, allowedValues] of groupedDependencies.entries()) {
              if (!allowedValues.includes(selectedCustomizations[type])) {
                allGroupsMet = false;
                break;
              }
            }
            availabilityMap[group.type][option.value] = allGroupsMet;
          }
        });
    });
    const finalSelectedOptions: string[] = [];
    for (const type in selectedCustomizations) {
      if (
        type !== "Accessories" &&
        type !== "Color" &&
        selectedCustomizations[type]
      ) {
        finalSelectedOptions.push(selectedCustomizations[type]);
      }
    }

    if (isPremiumEnabled) {
      const selectedMotor = selectedCustomizations["Motor Type"] || "";
      const isDoubleMotor = selectedMotor.toLowerCase().includes("double");
      const accessoriesGroup = product.customization?.find(
        (custom) => custom.type === "Accessories"
      );
      const premiumOption = accessoriesGroup?.options.find((opt) => {
        const optionValue = opt.value.toLowerCase();
        return (
          optionValue.includes("premium") &&
          (isDoubleMotor
            ? optionValue.includes("double")
            : optionValue.includes("single"))
        );
      });

      if (premiumOption) {
        finalSelectedOptions.push(premiumOption.value); // Use exact value from product data
      }
    }

    const priceBreakdown = calculatePrice(
      product,
      finalSelectedOptions,
      isWholesaleUser
    );
    const selectedMotor = selectedCustomizations["Motor Type"] || "";
    const isDoubleMotor = selectedMotor.toLowerCase().includes("double");

    const accessoriesGroup = product.customization?.find(
      (custom) => custom.type === "Accessories"
    );

    const singleMotorPremium = accessoriesGroup?.options.find((opt) => {
      const lowerValue = opt.value.toLowerCase();
      return lowerValue.includes("premium") && lowerValue.includes("single");
    });

    const doubleMotorPremium = accessoriesGroup?.options.find((opt) => {
      const lowerValue = opt.value.toLowerCase();
      return lowerValue.includes("premium") && lowerValue.includes("double");
    });

    const premiumDisplayCost = isWholesaleUser
      ? isDoubleMotor
        ? doubleMotorPremium?.wholeSaleAdditionalCost
        : singleMotorPremium?.wholeSaleAdditionalCost
      : isDoubleMotor
      ? doubleMotorPremium?.additionalCost
      : singleMotorPremium?.additionalCost;

    const premiumPackageData = {
      isAvailable: !!(singleMotorPremium || doubleMotorPremium),
      cost: premiumDisplayCost || 0,
      label: isDoubleMotor ? "(Double motor)" : "(Single motor)",
    };
    const currentSelections = Object.values(selectedCustomizations);
    const availableColors = (product.colors || []).filter((color) => {
      if (color.availableFor && color.availableFor.length > 0) {
        const hasAvailableMatch = currentSelections.some((selectedValue) =>
          color.availableFor.includes(selectedValue)
        );
        if (!hasAvailableMatch) return false;
      }
      return true;
    });
    const selectedColorName = selectedCustomizations["Color"];
    let isSelectedColorOutOfStock = false;

    if (selectedColorName && product.colors) {
      const selectedColor = product.colors.find(
        (c) => c.name === selectedColorName
      );

      if (
        selectedColor &&
        selectedColor.inStockFor &&
        selectedColor.inStockFor.length > 0
      ) {
        const isAvailable = currentSelections.some((selectedValue) =>
          selectedColor.inStockFor!.includes(selectedValue)
        );
        isSelectedColorOutOfStock = !isAvailable;
      }
    }
    const finalDimensions = calculateFinalDimensions(
      product,
      currentSelections
    );
    const imageList: string[] = [];
    let variantImageFound = false;
    const selectedColor = product.colors?.find(
      (c) => c.name === selectedCustomizations["Color"]
    );
    if (
      selectedColor &&
      selectedColor.variantImages &&
      selectedColor.variantImages.length > 0
    ) {
      const currentSelections = Object.values(selectedCustomizations);

      let bestMatch = null;
      let maxMatchCount = 0;

      for (const variant of selectedColor.variantImages) {
        if (!variant.requiredOptions || variant.requiredOptions.length === 0)
          continue;

        const isMatch = variant.requiredOptions.every((reqOpt) =>
          currentSelections.includes(reqOpt)
        );

        if (isMatch && variant.requiredOptions.length > maxMatchCount) {
          bestMatch = variant;
          maxMatchCount = variant.requiredOptions.length;
        }
      }

      if (bestMatch) {
        imageList.push(...bestMatch.imageUrls);
        variantImageFound = true;
      }
    }

    if (!variantImageFound) {
      for (const type in selectedCustomizations) {
        if (type === "Color") continue;
        const value = selectedCustomizations[type];
        const optionData = getOptionData(product, type, value);
        if (
          optionData?.customizationSpecificImageUrls &&
          optionData.customizationSpecificImageUrls.length > 0
        ) {
          imageList.push(...optionData.customizationSpecificImageUrls);
        }
      }
    }

    if (selectedColor?.imageUrls && selectedColor.imageUrls.length > 0) {
      imageList.push(...selectedColor.imageUrls);
    }

    if (product.commonImageUrls && product.commonImageUrls.length > 0) {
      imageList.push(...product.commonImageUrls);
    }

    const uniqueImages = [...new Set(imageList)];
    const finalImageList =
      uniqueImages.length > 0 ? uniqueImages : ["/placeholder.png"];

    return {
      availabilityMap,
      priceBreakdown,
      premiumPackageData,
      availableColors,
      isSelectedColorOutOfStock,
      finalDimensions,
      imageList: finalImageList,
    };
  }, [product, selectedCustomizations, isPremiumEnabled, isWholesaleUser]);
};
